<script setup lang="ts">
import type {
  IntegrationPhaseSteps,
  IntegrationStep,
} from "@/types/integration"
import type { UseStepperReturn } from "@vueuse/core"
import HeaderStep from "@/components/FileImport/Dialog/HeaderStep.vue"
import { computed, toRef } from "vue"

interface Props {
  stepper: UseStepperReturn<any, any[], any>
  tooltipText?: string
  running: boolean
}

interface Emits {
  (event: "next", step: IntegrationPhaseSteps): void
  (event: "step", step: IntegrationPhaseSteps): void
  (event: "finish"): void
}
const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const stepper = toRef(props, "stepper")

const showNextButton = computed(() => {
  return !stepper.value.isLast.value && stepper.value.current.value.showNext()
})

const showSaveButton = computed(() => {
  return stepper.value.isLast.value
})

function handleNext() {
  emit("next", stepper.value.next.value.title)
}

function handleImport() {
  emit("finish")
}

function handleStepClick(step: IntegrationStep) {
  emit("step", step.title)
}

function isStepDisabled(step: IntegrationStep) {
  return (
    !showNextButton.value &&
    !showSaveButton.value &&
    !stepper.value.isCurrent(step)
  )
}
</script>

<template>
  <h1 class="fixed font-vastago text-xl font-bold">
    Configure integration
  </h1>

  <div class="flex items-center justify-center">
    <div
      class="flex items-center gap-6 rounded-lg bg-white px-4 py-1 drop-shadow-md"
    >
      <div class="flex items-center gap-6">
        <button
          v-for="(step, stepIndex) in stepper.steps.value"
          :key="step.id"
          v-tooltip.icon="isStepDisabled(step) ? tooltipText : ''"

          :class="{
            'text-gray-700': isStepDisabled(step),
          }"
          :disabled="isStepDisabled(step)"
          @click="handleStepClick(step)"
        >
          <HeaderStep
            :title="`${stepIndex + 1}.${step.title}`"
            :decorator="step.decorator()"
            :is-current="stepper.isCurrent(step)"
          />
        </button>
      </div>

      <KButton
        v-if="showNextButton"
        size="small"
        class="h-6 px-7"
        @click="handleNext"
      >
        Next
      </KButton>
      <KButton
        v-if="showSaveButton"
        size="small"
        class="flex h-6 shrink-0 px-7"
        :disabled="running"
        @click="handleImport"
      >
        Run integration
      </KButton>
    </div>
  </div>
</template>
